import { elements } from '../../../dom';
import type { ImageFitId, ImageFocalPoint, ImagePlacement } from '../types';
import { setActive, setActiveBySelector } from '../../../utils/button-group';

type ImagePlacementControlsOptions = {
  onFitChange: (fit: ImageFitId) => void;
  onFocalPointChange: (focalPoint: ImageFocalPoint) => void;
  onZoomChange: (zoom: number) => void;
};

export function createImagePlacementControls({ onFitChange, onFocalPointChange, onZoomChange }: ImagePlacementControlsOptions) {
  let setupDisabled = false;

  document.querySelectorAll<HTMLButtonElement>('[data-image-fit]').forEach((button) => {
    button.addEventListener('click', () => {
      if (setupDisabled) {
        return;
      }

      const fit = button.dataset.imageFit as ImageFitId;
      setActive('[data-image-fit]', button);
      onFitChange(fit);
    });
  });

  elements.imageZoomSlider.addEventListener('input', () => {
    if (setupDisabled) {
      return;
    }

    const zoom = normalizeZoom(elements.imageZoomSlider.value);
    updateImageZoomLabel(zoom);
    onZoomChange(zoom);
  });

  document.querySelectorAll<HTMLButtonElement>('[data-focal-point]').forEach((button) => {
    button.addEventListener('click', () => {
      if (setupDisabled) {
        return;
      }

      const focalPoint = parseFocalPoint(button.dataset.focalPoint);
      if (!focalPoint) {
        return;
      }

      setActive('[data-focal-point]', button);
      onFocalPointChange(focalPoint);
    });
  });

  return {
    setPlacement(placement: ImagePlacement) {
      setActiveBySelector(`[data-image-fit="${placement.fit}"]`, '[data-image-fit]');
      elements.imageZoomSlider.value = String(placement.zoom);
      updateImageZoomLabel(placement.zoom);
      setActiveBySelector(`[data-focal-point="${formatFocalPointValue(placement.focalPoint)}"]`, '[data-focal-point]');
    },
    setSetupDisabled(isDisabled: boolean) {
      setupDisabled = isDisabled;
      elements.imageZoomSlider.disabled = isDisabled;
      document.querySelectorAll<HTMLButtonElement>('[data-image-fit], [data-focal-point]').forEach((button) => {
        button.disabled = isDisabled;
      });
    },
    updateVisibility(hasImage: boolean) {
      elements.imagePlacementControls.hidden = !hasImage;
    },
  };
}

function normalizeZoom(value: string) {
  const zoom = Number(value);
  if (!Number.isFinite(zoom)) {
    return 1;
  }
  return Math.min(2.5, Math.max(0.5, zoom));
}

function updateImageZoomLabel(zoom: number) {
  elements.imageZoomValue.textContent = `${Math.round(zoom * 100)}%`;
}

function parseFocalPoint(value: string | undefined): ImageFocalPoint | null {
  const [x, y] = value?.split(',').map(Number) ?? [];
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
  };
}

function formatFocalPointValue(focalPoint: ImageFocalPoint) {
  return `${focalPoint.x},${focalPoint.y}`;
}
